import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { SessionResponse } from '@assessments/shared'
import { apiFetch } from '../../services/apiClient.js'
import { runExitHooks } from '../../services/exitHooks.js'
import { logout as logoutApi, sessionEndTarget } from './api.js'

interface SessionState {
  session: SessionResponse | null
  loading: boolean
  error: string | null
  logout: () => Promise<void>
  /** Record that the platform session ended, surfacing the session-expired view. */
  onSessionExpired: () => void
}

/** The fetched half of the context — what the provider actually stores in state. */
type SessionData = Pick<SessionState, 'session' | 'loading' | 'error'>

const SessionContext = createContext<SessionState>({
  session: null,
  loading: true,
  error: null,
  logout: async () => {},
  onSessionExpired: () => {},
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionData>({
    session: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let controller = new AbortController()

    function fetchSession() {
      controller = new AbortController()
      apiFetch('/auth/session', { signal: controller.signal })
        .then(async (res) => {
          if (res.status === 401) {
            setState({ session: null, loading: false, error: 'unauthorized' })
            return
          }
          if (res.status === 403) {
            setState({ session: null, loading: false, error: 'forbidden' })
            return
          }
          if (!res.ok) throw new Error(`Session fetch failed: ${res.status}`)
          const data = (await res.json()) as SessionResponse
          setState({ session: data, loading: false, error: null })
        })
        .catch((err: unknown) => {
          if ((err as Error).name === 'AbortError') return
          setState({ session: null, loading: false, error: String(err) })
        })
    }

    fetchSession()

    function handlePageShow(event: PageTransitionEvent) {
      if (event.persisted) {
        controller.abort()
        fetchSession()
      }
    }

    window.addEventListener('pageshow', handlePageShow)

    return () => {
      controller.abort()
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [])

  async function logout() {
    // Run teardown BEFORE the platform logout POST: once the platform invalidates the
    // session cookie, any request this app still needs to make (releasing a held
    // section lock) comes back 401. Never awaited past its own timeout, so a slow API
    // cannot trap the user in a session they asked to leave.
    await runExitHooks()
    try {
      const data = await logoutApi()
      window.location.href = data?.url.split('?')[0] ?? sessionEndTarget()
    } catch {
      window.location.href = sessionEndTarget()
    }
  }

  function onSessionExpired() {
    setState({ session: null, loading: false, error: 'unauthorized' })
  }

  return (
    <SessionContext.Provider value={{ ...state, logout, onSessionExpired }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): SessionState {
  return useContext(SessionContext)
}
